import * as _ from "lodash";

import { ErrorMessages } from "../constants";

export type BadRequestMessageOrder = "responseFirst" | "errorsFirst";

export interface ApiErrorMessageOptions {
  badRequestOrder?: BadRequestMessageOrder;
}

const hasHeader = (headers: unknown, key: string): boolean => {
  if (!headers || typeof headers !== "object") return false;
  return Object.keys(headers as Record<string, unknown>).some(
    (k) => k.toLowerCase() === key.toLowerCase()
  );
};

const getHeaderValue = (headers: unknown, key: string): unknown => {
  if (!headers || typeof headers !== "object") return undefined;
  const record = headers as Record<string, unknown>;
  const matchedKey = Object.keys(record).find(
    (k) => k.toLowerCase() === key.toLowerCase()
  );
  return matchedKey ? record[matchedKey] : undefined;
};

const getBadRequestMessage = (
  err: any,
  order: BadRequestMessageOrder,
  fallbackMessage: string
): string => {
  const candidates: unknown[] =
    order === "errorsFirst"
      ? [
          _.get(err, "errors[0].message"),
          _.get(err, "response.data.errors[0].message"),
        ]
      : [
          _.get(err, "response.data.errors[0].message"),
          _.get(err, "errors[0].message"),
        ];

  const first = candidates.find((v) => typeof v === "string" && v.trim().length > 0);
  return (first as string | undefined) ?? fallbackMessage;
};

const getMessageFromPayload = (err: any): string | undefined => {
  const messageFromPayload: unknown =
    _.get(err, "response.data.errors[0].message") ?? _.get(err, "errors[0].message");
  if (typeof messageFromPayload === "string" && messageFromPayload.trim().length > 0) {
    return messageFromPayload;
  }
  return undefined;
};

const getMessageFromResponseData = (err: any): string | undefined => {
  const responseData: unknown = _.get(err, "response.data");
  if (typeof responseData === "string" && responseData.trim().length > 0) {
    return responseData;
  }
  return undefined;
};

const getMessageFromHeaders = (err: any): string | undefined => {
  const headers: unknown = _.get(err, "response.headers");
  if (!hasHeader(headers, "x-error-message")) return undefined;
  
  const headerMessage = getHeaderValue(headers, "x-error-message");
  if (typeof headerMessage === "string" && headerMessage.trim().length > 0) {
    return headerMessage;
  }
  return undefined;
};

export const getApiErrorMessage = (
  error: unknown,
  fallbackMessage: string,
  options: ApiErrorMessageOptions = {}
): string => {
  const err: any = error as any;
  const status = _.get(err, "response.status");

  if (status === 500) return ErrorMessages.serverError;
  if (status === 503) return ErrorMessages.deploymentInprogressError;

  if (status === 400) {
    const order: BadRequestMessageOrder = options.badRequestOrder ?? "responseFirst";
    return getBadRequestMessage(err, order, fallbackMessage);
  }

  return (
    getMessageFromPayload(err) ??
    getMessageFromResponseData(err) ??
    getMessageFromHeaders(err) ??
    fallbackMessage
  );
};

export const isRequestCanceled = (error: unknown): boolean => {
  const err: any = error as any;

  // DOM AbortController / fetch
  if (err?.name === "AbortError") return true;

  // Axios v1 cancellation
  if (err?.name === "CanceledError") return true;
  if (err?.code === "ERR_CANCELED") return true;

  const message: unknown = err?.message;
  if (typeof message === "string" && /cancell?ed/i.test(message)) return true;

  return false;
};

export const throwApiError = (
  error: unknown,
  fallbackMessage: string,
  options?: ApiErrorMessageOptions
): never => {
  // Preserve cancellation semantics so UI can ignore aborted requests.
  // Previously we converted cancellations into the fallback string which caused
  // false error notifications during screen/tab navigation.
  if (isRequestCanceled(error)) {
    const abortError = new Error("Request aborted");
    abortError.name = "AbortError";
    throw abortError;
  }

  throw getApiErrorMessage(error, fallbackMessage, options);
};
